import { FC, useMemo, useState } from 'react';

import { BackgroundsSlider } from './backgrounds-slider';
import { BannerModel } from '@/logic/services';
import { Box } from '@mui/material';
import { InfosSlider } from './infos-slider';
import { PostersSlider } from './posters-slider';
import SwiperInterface from 'swiper';

export interface HomeSliderProps {
  banners: BannerModel[];
}

export interface ContentSliderProps extends HomeSliderProps {
  swiper: SwiperInterface;
  handleSwiper: (swiper: SwiperInterface) => void;
}

// The sliders reserve their space up front so a slow image load inside a
// slide cannot shift everything below it once it decodes. See the git
// history for the derivation of these numbers per breakpoint:
//
//   laptop  44rem backgrounds - 25rem overlap + (100vw/5 * 1.5) posters + 80px poster padding
//   mobile  165px backgrounds -  90px overlap + (100vw/3 * 1.5) posters + 80px poster padding + 225px infos
const reservedHeight = {
  mobile: 'calc(380px + 50vw)',
  laptop: 'calc(19rem + 80px + 30vw)',
};

// Swiper (v8, swiper/react) initializes via useIsomorphicLayoutEffect and
// renders plain slide markup otherwise, so it is SSR-safe: there is no
// window/document access at render time. This used to be gated behind a
// mount-after-hydration useEffect, which meant the LCP hero image never
// existed in the server HTML — only the <link rel=preload> did — so the
// browser could not paint it until the JS bundle downloaded, parsed, and
// hydrated. Rendering it directly here lets it paint from the initial HTML.
export const HomeSlider: FC<HomeSliderProps> = ({ banners }) => {
  const [postersSwiper, setPostersSwiper] = useState<SwiperInterface | null>(null);
  const [backgroundsSwiper, setBackgroundsSwiper] = useState<SwiperInterface | null>(
    null
  );
  const [infosSwiper, setInfosSwiper] = useState<SwiperInterface | null>(null);

  // A fresh tuple here every render made Swiper's Controller module re-diff
  // its `control` param on each parent render; stable identity binds it once
  // per actual swiper-instance change.
  const postersControlledSwipers = useMemo(
    () => [backgroundsSwiper, infosSwiper],
    [backgroundsSwiper, infosSwiper]
  );

  return (
    <Box
      component='section'
      sx={{
        minHeight: reservedHeight,
      }}
    >
      <BackgroundsSlider
        banners={banners}
        swiper={postersSwiper}
        handleSwiper={setBackgroundsSwiper}
      />
      <PostersSlider
        banners={banners}
        swiper={postersControlledSwipers}
        handleSwiper={setPostersSwiper}
      />
      <InfosSlider
        banners={banners}
        swiper={postersSwiper}
        handleSwiper={setInfosSwiper}
      />
    </Box>
  );
};
